import { CUSTOMER_RBAC_ROLE_LABELS, isPropertyManagerRole } from '@metron/shared';
import type { CustomerUserResponse } from '@metron/shared';
import type { UserWithCustomers } from '../../hooks/useUsers.ts';
import { statusOption } from '../../lib/user-status.ts';
import type {
  CustomerState,
  ResolvedCustomer,
  RoleState,
  UsersTableRow,
  UserTypeState,
} from './types.ts';

/**
 * A customer as it appears in a resolved-name lookup. Only the id is used for
 * membership; the name is display only.
 */
export interface CustomerNameEntry {
  id: number;
  name: string;
}

/** The `['customers']` query indexed by id, built by the caller. */
export type CustomersById = ReadonlyMap<number, CustomerNameEntry>;

function customerData(user: UserWithCustomers): CustomerUserResponse | undefined {
  return user.customerData;
}

/**
 * Whether this user's Cognito groups were resolved.
 *
 * Read as `!== false`, never `!value`, so a payload without the field (an older
 * deployment, or a single-user response) counts as resolved rather than
 * regressing every row to "Unknown".
 */
export function isGroupsResolved(user: UserWithCustomers): boolean {
  return customerData(user)?.groupsResolved !== false;
}

/**
 * Every customer id this user belongs to: their direct grants ∪ the distinct
 * customers owning their assigned properties. Ids only — never a name string,
 * which is what used to make `/users` and the customer page disagree about the
 * same user.
 */
export function customerIdsFor(user: UserWithCustomers): number[] {
  const cd = customerData(user);
  const ids: number[] = [];
  const add = (id: number | null | undefined) => {
    if (typeof id === 'number' && !ids.includes(id)) ids.push(id);
  };

  for (const grant of cd?.customers ?? []) add(grant.id);
  for (const property of cd?.properties ?? []) add(property.customerId);

  return ids;
}

/**
 * The user's customers as `{id, name}`, ordered by name (then id) so the
 * "multiple" cell has a stable order.
 *
 * An id present in a grant but absent from `customersById` still counts as a
 * grant — dropping it would render a granted user as "Unassigned". Its name
 * falls back to the id as a string, and `UsersTableRow.unresolvedCustomerIds`
 * records it so the UI can flag it.
 */
export function resolveCustomers(
  user: UserWithCustomers,
  customersById: CustomersById,
): ResolvedCustomer[] {
  return customerIdsFor(user)
    .map((id) => ({ id, name: customersById.get(id)?.name ?? String(id) }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.id - b.id);
}

/** Customer ids the `['customers']` query could not resolve. */
export function unresolvedCustomerIds(
  user: UserWithCustomers,
  customersById: CustomersById,
): number[] {
  return customerIdsFor(user).filter((id) => !customersById.has(id));
}

/** Type column: Metron / External, or Unknown when the group lookup failed. */
export function deriveType(user: UserWithCustomers): UserTypeState {
  if (!isGroupsResolved(user)) return 'unknown';
  return customerData(user)?.isMetronInternal === true ? 'metron' : 'external';
}

/**
 * Customer column. With no customers at all the cell depends on the Type: a
 * Metron user needs none ("N/A"), anyone else is genuinely "Unassigned".
 */
export function deriveCustomerState(
  type: UserTypeState,
  customers: ResolvedCustomer[],
): CustomerState {
  if (customers.length > 1) return { kind: 'multiple', customers };
  if (customers.length === 1) return { kind: 'named', customers };
  return type === 'metron' ? { kind: 'na' } : { kind: 'unassigned' };
}

/**
 * True for the admin-style RBAC roles — today `utility-admin`, `mhp-admin` and
 * `cmfi-admin`. Expressed as "a known customer role that is not a
 * property-manager role" so a new admin role is covered automatically.
 */
export function isAdminRbacRole(role: string | null | undefined): boolean {
  if (role == null) return false;
  if (isPropertyManagerRole(role)) return false;
  return role in CUSTOMER_RBAC_ROLE_LABELS;
}

/** Inputs `deriveRoleState` needs beyond the payload row. */
export interface RoleDerivationContext {
  type: UserTypeState;
  propertyCount: number;
  /** True when the user has at least one customer grant or property. */
  hasAssignment: boolean;
}

/**
 * Role column. Derived from the Cognito RBAC group and property assignments
 * only — no account-type default.
 *
 * Precedence:
 * 1. group lookup failed          → `unknown` (a null role is then a failure,
 *                                   not the fact that no role is assigned)
 * 2. end-consumer group           → `endConsumer`
 * 3. `*-property-mgr` group       → `propertyManager`
 * 4. admin group                  → `admin`, still carrying `propertyCount` so
 *                                   the cell can show both signals
 * 5. properties but no group      → `propertyManager`
 * 6. Metron                       → `metronInternal` ("Metron Internal")
 * 7. External with an assignment  → `notAssigned`
 * 8. otherwise                    → `none` ("—"): an External user with no
 *                                   assignment and no role
 *
 * Step 6 comes before step 7 on purpose: a Metron user's access does not come
 * from a customer RBAC role, so "Not assigned" would assert a missing grant
 * that is not missing. With or without a bare assignment they read "Metron
 * Internal"; only a real group (steps 2–5) overrides it.
 */
export function deriveRoleState(
  user: UserWithCustomers,
  { type, propertyCount, hasAssignment }: RoleDerivationContext,
): RoleState {
  if (!isGroupsResolved(user)) return { kind: 'unknown' };

  const cd = customerData(user);
  if (cd?.isEndConsumer === true) return { kind: 'endConsumer' };

  const role = cd?.rbacRole ?? null;
  if (isPropertyManagerRole(role)) return { kind: 'propertyManager', propertyCount };
  if (isAdminRbacRole(role)) return { kind: 'admin', propertyCount };
  if (propertyCount > 0) return { kind: 'propertyManager', propertyCount };

  if (type === 'metron') return { kind: 'metronInternal' };
  if (hasAssignment && type === 'external') return { kind: 'notAssigned' };
  return { kind: 'none' };
}

/** The exact string the Type cell renders. */
export function typeLabel(type: UserTypeState): string {
  switch (type) {
    case 'metron':
      return 'Metron';
    case 'external':
      return 'External';
    case 'unknown':
      return 'Unknown';
  }
}

/** The exact string the Customer cell renders. */
export function customerCellText(state: CustomerState): string {
  switch (state.kind) {
    case 'named':
    case 'multiple':
      return state.customers.map((c) => c.name).join(', ');
    case 'na':
      return 'N/A';
    case 'unassigned':
      return 'Unassigned';
  }
}

/**
 * The exact string the Role cell renders. A property count is folded into the
 * label as a suffix — "Property Manager (3)" — rather than shown as a separate
 * badge (review, 2026-09-04). TanStack's alphanumeric sort orders the embedded
 * number numerically, so "(2)" still sorts before "(10)".
 */
function withCount(label: string, count: number): string {
  return count > 0 ? `${label} (${count})` : label;
}

export function roleLabel(state: RoleState): string {
  switch (state.kind) {
    case 'admin':
      return withCount('Admin', state.propertyCount);
    case 'propertyManager':
      return withCount('Property Manager', state.propertyCount);
    case 'endConsumer':
      return 'End consumer';
    case 'metronInternal':
      return 'Metron Internal';
    case 'notAssigned':
      return 'Not assigned';
    case 'none':
      return '—';
    case 'unknown':
      return 'Unknown';
  }
}

/** Composes every derivation into one row. The only function the hook calls. */
export function toRow(user: UserWithCustomers, customersById: CustomersById): UsersTableRow {
  const cd = customerData(user);
  const properties = cd?.properties ?? [];
  const propertyCount = properties.length;

  const type = deriveType(user);
  const customers = resolveCustomers(user, customersById);
  const customerState = deriveCustomerState(type, customers);
  const roleState = deriveRoleState(user, {
    type,
    propertyCount,
    hasAssignment: customers.length > 0,
  });
  const status = statusOption(user);

  return {
    user,
    username: user.username,
    email: user.email,
    name: user.name,
    enabled: user.enabled,
    userStatus: user.userStatus,
    isFederated: user.isFederated,
    createdAt: user.createdAt,
    lastLoginAt: user.lastLoginAt ?? null,

    type,
    customers,
    customerState,
    roleState,
    propertyCount,
    propertyNames: properties.map((p) => p.name),
    isEndConsumer: cd?.isEndConsumer === true,
    groupsResolved: isGroupsResolved(user),
    unresolvedCustomerIds: unresolvedCustomerIds(user, customersById),

    status,

    sortKeys: {
      type: typeLabel(type),
      customer: customerCellText(customerState),
      role: roleLabel(roleState),
      status: status.rank,
    },
  };
}

/**
 * Membership predicate for `mode: 'customer'`, by id — the same rule the
 * customer page uses today, re-expressed over the resolved set.
 */
export function belongsTo(row: UsersTableRow, customerId: number): boolean {
  return row.customers.some((c) => c.id === customerId);
}
